import Vue from 'vue'
import Hookable from 'hookable'
import isNil from 'lodash.isnil'
import isUndefined from 'lodash.isundefined'
import cloneDeep from 'lodash.clonedeep'
import isString from 'lodash.isstring'
import find from 'lodash.find'
import set from 'lodash.set'
import { DateTime } from 'luxon'

//#region Main Classes
/**
 * Type class
 */
class Type {
  constructor (object, type) {
    Object.assign(this, { _type: type || this.constructor.alias }, object)
  }

  static _types = {}
  static registerAllTypes () {
    TYPES.forEach((type) => {
      const Class = new type.className()
      this._types[type.alias] = Class
    })
  }

  static from (object, type) {
    return new this(object, type)
  }

  static fromJson (json, includedItems) {
    return Array.isArray(json)
      ? json.map(j => Type._fromJson(j, includedItems))
      : Type._fromJson(json, includedItems)
  }

  static _fromJson (json, includedItems = []) {
    const typeExists = Boolean(Type._types[json.type])
    let res = {
      _type: json.type,
      id: json.id,
      ...json.attributes
    }

    if (json.links) {
      res.links = json.links
    }

    if (json.relationships) {
      Object.keys(json.relationships).forEach((rel) => {
        res[rel] = Type.fromJson(
          json.relationships[rel].data,
          includedItems
        )
      })
    }

    const includedItemIdx = includedItems.findIndex(
      i => i.id === json.id && i.type === json.type
    )
    const includedItem = includedItems[includedItemIdx]

    if (includedItem) {
      res = {
        ...res,
        ...includedItem.attributes
      }

      if (includedItem.relationships) {
        Object.keys(includedItem.relationships).forEach((rel) => {
          res[rel] = Type.fromJson(
            includedItem.relationships[rel].data,
            spliceNoMutate(includedItems, includedItemIdx)
          )
        })
      }
	}

    return typeExists ? new (TYPES.find(t => t.alias === json.type).className)(res) : res
  }

  equals (other) {
    if (this.id === undefined) {
      return false
    } else if (typeof other === 'string') {
      return other === this.id
    } else if (typeof other === 'object' && other !== null) {
      return other.id === this.id
    } else {
      return false
    }
  }

  toJson () {
    const res = {}

    Object.keys(this).forEach((key) => {
      const value = this[key]
      const shouldHandleAsTypeArray =
        Array.isArray(value) &&
        value.length > 0 &&
        value[0] instanceof Type

      if (shouldHandleAsTypeArray) {
        res.relationships = {
          ...res.relationships,
          [key]: {
            data: value.map(v => v.toJson())
          }
        }
      } else if (value instanceof Type) {
        res.relationships = {
          ...res.relationships,
          [key]: {
            data: value.toJson()
          }
        }
      } else if (key === 'id') {
        res.id = value
      } else if (key === '_type') {
        res.type = value
      } else if (key === 'links') {
        res.links = value
      } else {
        res.attributes = {
          ...res.attributes,
          [key]: value
        }
      }
    })

    return res
  }

  clone () {
    return new this.constructor(cloneDeep(this), this.type)
  }

  /**
   * This method is specific for vue-ssr, which calls this method if it exists
   * in order to serialize objects from the store server side to be hydrated
   * on the client side.
   *
   * In turn, we have to re-instantiate the types in the store after hydration,
   * this happens in the `api/index.js` class.
   */
  toJSON () {
    return Object.assign({}, this)
  }
}

/**
 * FilterVisitor class
 */
class FilterVisitor {
	results = []

	constructor(predicate) {
		this.predicate = predicate
	}

	visit(segment) {
		if (this.predicate(segment)) {
			this.results.push(segment)
		}
	}
}

/**
 * SegmentNode class
 */
class SegmentNode extends Type {
	getSegmentsByType(type) {
		const filterVisitor = new FilterVisitor(
			segment => segment.type.name.toLowerCase() === type.toLowerCase()
		)
		this.accept(filterVisitor)
		return filterVisitor.results
	}

	getAllSegments() {
		const filterVisitor = new FilterVisitor(s => true)
		this.accept(filterVisitor)
		return filterVisitor.results
	}

	getSegmentById(id) {
		const filterVisitor = new FilterVisitor(segment => segment.id === id)
		this.accept(filterVisitor)
		return filterVisitor.results[0]
	}

	getSegmentAndAncestry(id) {
		const segment = this.getSegmentById(id)

		if (segment) {
			const parent = this.getParentSegmentByChildId(id)

			if (parent) {
				return [segment, ...this.getSegmentAndAncestry(parent.id)]
			} else {
				return [segment]
			}
		} else {
			return []
		}
	}

	/*
	 * Allows to get the parent for a certain segment ID
	 * Example: getParentForSegmentById('1687a774-4c45-11ea-8230-0242ac1d0003') => 'XBOX'
	 */
	getParentSegmentByChildId(id) {
		const filterVisitor = new FilterVisitor(segment =>
			segment?.children?.find(segment => segment.id === id)
		)
		this.accept(filterVisitor)
		return filterVisitor.results[0]
	}

	/*
	 * This function allows to do the following:
	 *	const segment = this.event
	 *						.getSegment(s => s.name === 'Online Qualification')
	 *						.getSegment(s => s.name === this.selectedPlatform.toUpperCase())
	 *						.getSegment(s => s.name === this.clubId)
	 */
	getSegment(fn) {
		const filterVisitor = new FilterVisitor(fn)
		this.accept(filterVisitor)
		return filterVisitor.results[0]
	}

	/*
	 * This function takes an Array, String or Number
	 * If an Array is provided it will match the element of the array with each level of segment
	 * 		If an Object is provided lodash.find to match all keys will be used (this is case sensitive)
	 *		If a String is provided we will check for a match on id or name (case unsensitive)
	 *		If a Number is provided the index will be selected
	 *			Example: getSegmentByPath([0, 'xbox', { name: 'Chelsea', eliminationType: 'ladder/time_oriented' }])
	 *
	 *
	 * If a String is provided we will check for a match on id or name (case unsensitive)
	 *		Example: getSegmentByPath('Online Qualification')
	 *
	 * If a Number is provided the index will be selected
	 * 		Example: getSegmentByPath(0)
	 *
	 */
	getSegmentByPath(path) {
		if (!this && !this.children) {
			return null
		}

		let segments = this.children
		if (Array.isArray(path)) {
			let segment = null
			path.forEach(pathElement => {
				if (isString(pathElement)) {
					segment = this.getSegmentByNameOrId(segments, pathElement)
				} else if (Number.isInteger(pathElement)) {
					segment = this.getArrayIndex(segments, pathElement)
				} else {
					segment = find(segments, pathElement)
				}

				if (!segment) {
					return null
				}
				segments = segment.children
			})
			return segment
		} else if (isString(path)) {
			return this.getSegmentByNameOrId(segments, path)
		} else if (Number.isInteger(path)) {
			return this.getArrayIndex(segments, path)
		}
	}

	getArrayIndex(arr, idx) {
		if (arr && idx >= 0 && idx < arr.length) {
			return arr[idx]
		} else {
			return null
		}
	}

	getSegmentByNameOrId(segments, val) {
		return segments.find(
			segment =>
				segment.id.toLowerCase() === val.toLowerCase() ||
				segment.name.toLowerCase() === val.toLowerCase()
		)
	}
}

/**
 * APIError class
 */

class APIError extends Error {
  constructor (err) {
      const method = err?.config?.method || 'unknown method'
      const path = err?.config?.url || 'unknown path'
      const status = err?.response?.status || 'unknown status'
      const statusText = err?.response?.statusText || 'unknown error'
      const message = getErrorMessage(err)

      super(`${status} ${method.toUpperCase()} ${path}\n${message}`)

      Object.assign(this, err)

      this.name = `${statusText.replace(/ /g, '')}APIError`

      this.method = method
      this.path = path
      this.status = status
  }
}

/**
 * Endpoint class
 */
class Endpoint {
  constructor (axios, baseUrl) {
    this.axios = axios
    this.baseUrl = baseUrl
  }
}
//#endregion

//#region Types
/**
 * Types
 */
class ActivityLogUserGrouped extends Type {
	static alias = 'activity-log-user-grouped'

	get compoundId() {
		return this.ipAddress + this.date.from
	}

	get formattedToDateTime() {
		return DateTime.fromSQL(this.date.to).toFormat(
			FORMATS.DATE_TIME
		)
	}

	get formattedFromDate() {
		return DateTime.fromSQL(this.date.from).toFormat(
			FORMATS.DATE_TIME
		)
	}
}

class ActivityLog extends Type {
	static alias = 'activity-log'

	get getAlias() {
		return this?.user?.alias
	}

	get formattedDateTime() {
		return DateTime.fromISO(this.date).toFormat(
			FORMATS.DATE_TIME
		)
	}

	get formattedDate() {
		return DateTime.fromISO(this.date).toFormat(FORMATS.DATE)
	}

	get formattedTime() {
		return DateTime.fromISO(this.date).toFormat(FORMATS.TIME)
	}
}

class AdvanceRule extends Type {
	static alias = 'advance-rules'

	static fromFormToJson(form) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(
				{
					placements: form.placements.split(',').map(n => +n)
				},
				'AdvanceRules'
			).toJson(),
			...{
				relationships: {
					nextSegment: {
						data: Type.from(
							{ id: form.nextSegmentId },
							'Segment'
						).toJson()
					}
				}
			}
		}
	}
}

class Attachment extends Type {
	static alias = 'attachments'

	get formattedDateTime() {
		return DateTime.fromISO(this.createdAt).toFormat(
			FORMATS.DATE_TIME
		)
	}
}

class Competitor extends Type {
	static alias = 'competitors'
}

class EmailAddress extends Type {
	static alias = 'email-addresses'

	get formattedCreatedDateTime() {
		return DateTime.fromISO(this.createdAt).toFormat(
			FORMATS.DATE_TIME
		)
	}
}

class Event extends SegmentNode {
	static alias = 'events'

	get children() {
		return this.segments || []
	}

	accept(visitor) {
		if (this.segments) {
			this.segments.forEach(segment => segment.accept(visitor))
		}
	}

	get formattedFromDateTime() {
		return DateTime.fromISO(this.date.from).toFormat(
			FORMATS.DATE_TIME
		)
	}

	get formattedToDateTime() {
		return DateTime.fromISO(this.date.to).toFormat(
			FORMATS.DATE_TIME
		)
	}

	get activeSegment() {
		const now = Date.now()
		return (
			this.segments.find(
				s => new Date(s.date.from) < now && new Date(s.date.to) > now
			) || this.segments[this.segments.length - 1]
		)
	}

	get isActive() {
		return this.status === 'active'
	}

	get isArchived() {
		return this.status === 'archived'
	}

	static fromFormToJson(form) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(
				{
					name: form.name,
					status: form.status,
					date: {
						from: DateTime.fromISO(form.date.from).toISO({
							suppressMilliseconds: true
						}),

						to: DateTime.fromISO(form.date.to).toISO({
							suppressMilliseconds: true
						})
					}
				},
				'Event'
			).toJson(),
			...{
				relationships: {
					game: {
						data: Type.from({ id: form.game.id }, 'Game').toJson()
					}
				}
			}
		}
	}
}

class Franchise extends Type {
	static alias = 'franchises'
}

class GameAccount extends Type {
	static alias = 'game-account'

	static fromFormToJson(form) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(
				{
					username: form.username
				},
				'game-accounts'
			).toJson()
		}
	}
}

class Game extends Type {
	static alias = 'games'
}

class MatchSeries extends Type {
  static alias = 'match-series'

  get homeCompetitor() {
    return this?.competitors?.[0]
  }

  get homeUser() {
    return this?.homeCompetitor?.roster?.players?.[0]?.user
  }

  get homeRosterName() {
    return this?.homeCompetitor?.roster?.name || this?.homeUser?.alias
  }

  get homeIsWinner() {
    return this?.homeCompetitor?.placement === 1
  }

  get homeScore() {
    return this?.homeCompetitor?.result?.score
  }

  homePeriodScore(periodName) {
    return this?.matches?.[0]?.periods.find(e => e.name === periodName)
      ?.competitors?.[0]?.result.score
  }

  get homeFFW() {
    return (
      this?.homeCompetitor?.result?.forfeit === false &&
          this?.awayCompetitor?.result?.forfeit === true
    )
  }

  set homeScore(value) {
    set(this, 'competitors[0].result.score', value)
  }

  get homeResult() {
    return this.homeFFW ? 'FFW' : this.isForfeitted ? 'FFL' : this.homeScore
  }

  get awayCompetitor() {
    return this?.competitors?.[1]
  }

  get awayUser() {
    return this?.awayCompetitor?.roster?.players?.[0]?.user
  }

  get awayIsWinner() {
    return this?.awayCompetitor?.placement === 1
  }

  get awayRosterName() {
    return (
      this?.awayCompetitor?.roster?.name || this?.awayUser?.alias || 'TBD'
    )
  }

  get awayScore() {
    return this?.awayCompetitor?.result?.score
  }

  awayPeriodScore(periodName) {
    return this?.matches?.[0]?.periods.find(e => e.name === periodName)
      ?.competitors?.[1]?.result.score
  }

  get awayFFW() {
    return (
      this?.awayCompetitor?.result?.forfeit === false &&
          this?.homeCompetitor?.result?.forfeit === true
    )
  }

  get awayResult() {
    return this.awayFFW ? 'FFW' : this.isForfeitted ? 'FFL' : this.awayScore
  }

  set awayScore(value) {
    set(this, 'competitors[1].result.score', value)
  }

  get scheduled() {
    return this.date !== null
  }

  get isForfeitted() {
    return this.homeFFW || this.awayFFW
  }

  get hasMatches() {
    return this.matches && this.matches.length > 0
  }

  get formattedDateTime() {
    return DateTime.fromISO(this.date).toFormat(
      FORMATS.DATE_TIME
    )
  }

  get formattedDate() {
    return DateTime.fromISO(this.date).toFormat(FORMATS.DATE)
  }

  get formattedTime() {
    return DateTime.fromISO(this.date).toFormat(FORMATS.TIME)
  }

  get formattedCreatedDateTime() {
    return DateTime.fromISO(this.createdAt).toFormat(
      FORMATS.DATE_TIME
    )
  }

  get timeDiffInHours() {
	  return DateTime.fromISO(this.date)
		  .diff(DateTime.local(), ['hours'])
		  .toObject().hours
  }

	get timeDiffInMinutes() {
	  return DateTime.fromISO(this.date)
		  .diff(DateTime.utc(), ['minutes'])
		  .toObject().minutes
  }

  static sortMatchesByDate(matches = []) {
		return [...matches].sort(
			(a, b) =>
				DateTime.fromISO(a.date)
					.diff(DateTime.fromISO(b.date), ['seconds'])
					.toObject().seconds
		)
  }

	static getFutureMatches(matches) {
		return [...matches].filter(
			m =>
				DateTime.fromISO(m.date)
					.diff(DateTime.local(), ['seconds'])
					.toObject().seconds >= 0
		)
  }

	static getPastMatches(matches) {
		return [...matches].filter(
			m =>
				DateTime.fromISO(m.date)
					.diff(DateTime.local(), ['seconds'])
					.toObject().seconds < 0
		)
  }

	static getNextUpcomingMatch(matches = []) {
		const futureMatches = this.getFutureMatches(matches)
		const futureMatchesByDate = this.sortMatchesByDate(futureMatches)
		return futureMatchesByDate.length > 0
			? futureMatchesByDate[0]
			: undefined
  }

	static getMostRecentMatch(matches = []) {
		const pastMatches = this.getPastMatches(matches)
		const pastMatchesByDate = this.sortMatchesByDate(pastMatches)
		return pastMatchesByDate.length > 0
			? pastMatchesByDate[pastMatchesByDate.length - 1]
			: undefined
	}
}

class Match extends Type {
	static alias = 'matches'
}

class Period extends Type {
	static alias = 'periods'

	get homeResult() {
		return this?.competitors?.[0]?.result?.score
	}

	get awayResult() {
		return this?.competitors?.[1]?.result?.score
	}
}

class PhoneNumber extends Type {
	static alias = 'phone-numbers'

	get formattedCreatedDateTime() {
		return DateTime.fromISO(this.createdAt).toFormat(
			FORMATS.DATE_TIME
		)
	}

	get formattedExpiresDateTime() {
		return DateTime.fromISO(this.expiresAt).toFormat(
			FORMATS.DATE_TIME
		)
	}
}

class Players extends Type {
	static alias = 'players'

	get alias() {
		return (
			this?.name ||
			this?.gameAccounts?.[0]?.username ||
			this?.game_accounts?.[0]?.username
		)
	}

	static fromFormToJson(form) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(null, 'players').toJson(),
			...{
				relationships: {
					user: {
						data: Type.from({ id: form.user }, 'users').toJson()
					}
				}
			}
		}
	}
}

class RegistrationPhase extends Type {
	static alias = 'registration-phases'

	get formattedFromDateTime() {
		return DateTime.fromISO(this.date.from).toFormat(
			FORMATS.DATE_TIME
		)
	}

	get formattedToDateTime() {
		return DateTime.fromISO(this.date.to).toFormat(
			FORMATS.DATE_TIME
		)
	}

	static fromFormToJson(form) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(
				{
					name: form.name,
					date: {
						from: DateTime.fromISO(form.date.from).toISO({
							suppressMilliseconds: true
						}),
						to: DateTime.fromISO(form.date.to).toISO({
							suppressMilliseconds: true
						})
					},
					type: form.type
				},
				'RegistrationPhase'
			).toJson()
		}
	}
}

class Role extends Type {
	static alias = 'roles'
}

class Roster extends Type {
	static alias = 'rosters'

	get firstGameAccountUsername() {
		return this?.owner?.gameAccounts?.[0]?.username || this?.owner?.username
	}

	get rosterName() {
		return this?.name || this.firstGameAccountUsername
	}

	get ownerAccount() {
		return this?.owner || 'N/A'
	}

	get formattedCreatedDateTime() {
		return DateTime.fromISO(this.createdAt).toFormat(
			FORMATS.DATE_TIME
		)
	}

	static fromFormToJson(form) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(
				{
					name: form.name
				},
				'Roster'
			).toJson(),
			...{
				relationships: {
					segment: {
						data: Type.from(
							{ id: form.segment.id },
							'Segment'
						).toJson()
					},
					users: {
						data: [Type.from({ id: form.user.id }, 'User').toJson()]
					}
				}
			}
		}
	}
}

class SegmentType extends Type {
	static alias = 'segment-types'
}

class Segment extends SegmentNode {
	static alias = 'segments'

	get hasChildren() {
		return this.children && this.children.length > 0
	}

	get logoUrl() {
		return this.getImage('logo')?.image?.default
	}

	getImage(type) {
		return this?.images?.filter(function(image) {
			return image.type === type
		})?.[0]
	}

	get formattedFromDateTime() {
		return DateTime.fromISO(this.date.from).toFormat(
			FORMATS.DATE_TIME
		)
	}

	get formattedToDateTime() {
		return DateTime.fromISO(this.date.to).toFormat(
			FORMATS.DATE_TIME
		)
	}

	accept(visitor) {
		if (this.children) {
			this.children.forEach(segment => segment.accept(visitor))
		}

		visitor.visit(this)
	}

	// eslint-disable-next-line max-lines-per-function
	static fromFormToJson(form, parentId, mode) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(
				{
					name: form.name,
					date: {
						from: DateTime.fromISO(form.date.from).toISO({
							suppressMilliseconds: true
						}),

						to: DateTime.fromISO(form.date.to).toISO({
							suppressMilliseconds: true
						})
					},
					format: form.format,
					eliminationType: form.eliminationType,
					registrationType: form.registrationType,
					timezone: form.timezone,
					configuration: form.configuration,
					...(form.rosterSize
						? {
								rosterSize: {
									playersMin: +form.rosterSize.playersMin,
									playersMax: +form.rosterSize.playersMax,
									coachesMax: +form.rosterSize.coachesMax
								}
						  }
						: {
								...(mode === 'create'
									? { rosterSize: null }
									: {})
						  }),
					...(form.rostersLimit
						? {
								rostersLimit: {
									limit: +form.rostersLimit.limit,
									standbyLimit: +form.rostersLimit
										.standbyLimit
								}
						  }
						: {
								...(mode === 'create'
									? { rostersLimit: null }
									: {})
						  })
				},
				'Segments'
			).toJson(),
			...{
				relationships: {
					parent: parentId
						? {
								data: Type.from(
									{ id: parentId },
									'Segment'
								).toJson()
						  }
						: null,
					type: {
						data: Type.from(
							{ id: form.type.id },
							'SegmentType'
						).toJson()
					}
				}
			}
		}
	}
}

class SupportedPeriod extends Type {
	static alias = 'supported-periods'

	static fromFormToJson(form) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(
				{
					name: form.name,
					key: form.key,
					sortOrder: parseInt(form.sortOrder),
					isRequired: parseInt(form.isRequired),
					isFinalResult: parseInt(form.isFinalResult)
				},
				'supported-periods'
			).toJson()
		}
	}
}

class Team extends Type {
	static alias = 'teams'

	get ownerName() {
		return this.owner?.name || 'N/A'
	}

	static fromFormToJson(form) {
		return {
			...(form.id ? { id: form.id } : {}),
			...Type.from(
				{
					name: form.name,
					game: {
						id: form.game.id
					}
				},
				'team'
			).toJson()
		}
	}
}

class User extends Type {
	static alias = 'user'

	get age() {
		const dob = DateTime.fromISO(this.dateOfBirth)
		const now = DateTime.local()

		return Math.floor(now.diff(dob, 'years').toObject().years)
	}

	get fullName() {
		if (this.firstName || this.lastName) {
			return `${this.firstName} ${this.lastName}`.trim()
		} else {
			return undefined
		}
	}

	get avatarTemplateSrc() {
		return this?.profiles?.[0]?.avatar?.template || this.avatar?.default
	}

	set avatarTemplateSrc(value) {
		set(this, 'profiles[0].avatar.template', value)
	}

	get alias() {
		return (
			this?.profiles?.[0]?.username ||
			this?.gameAccounts?.[0]?.username ||
			this?.game_accounts?.[0]?.username ||
			this?.username ||
			this?.fullName ||
			this?.email
		)
	}
}

class Token extends User {
	static alias = 'token'
}

class Users extends User {
	static alias = 'users'

	get permissionNames() {
		return this.permissions.map(p => p.permission).join(', ')
	}
}
//#endregion

//#region Endpoints
/**
 * Endpoints
 */
class ChatEndpoint extends Endpoint {
	get(params) {
		return this.axios.get(`${this.baseUrl}/history`, {
			params
		})
	}
}

//#region EventEndpoint

class EventEndpoint extends Endpoint {
	getAll(params) {
		return this.axios.get(`${this.baseUrl}/events`, {
			params
		})
	}

	getById(id) {
		return this.axios.get(`${this.baseUrl}/events/${id}`)
	}

	create(eventForm) {
		const data = Event.fromFormToJson(eventForm)

		return this.axios.post(`${this.baseUrl}/events`, {
			data
		})
	}

	update(eventForm) {
		const data = Event.fromFormToJson(eventForm)

		return this.axios.put(`${this.baseUrl}/events/${eventForm.id}`, {
			data
		})
	}

	clone(eventId) {
		return this.axios.post(`${this.baseUrl}/events/${eventId}/clone`)
	}

	delete(eventId) {
		return this.axios.delete(`${this.baseUrl}/events/${eventId}`)
	}
}

//#region RegistrationPhaseEndpoint

class RegistrationPhaseEndpoint extends Endpoint {
	update(eventId, segmentId, registrationPhaseForm) {
		const data = RegistrationPhase.fromFormToJson(registrationPhaseForm)

		return this.axios.put(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/registration-phases/${registrationPhaseForm.id}`,
			{
				data
			}
		)
	}

	create(eventId, segmentId, registrationPhaseForm) {
		const data = RegistrationPhase.fromFormToJson(registrationPhaseForm)

		return this.axios.post(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/registration-phases`,
			{
				data
			}
		)
	}

	delete(eventId, segmentId, registrationPhaseId) {
		return this.axios.delete(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/registration-phases/${registrationPhaseId}`
		)
	}
}

//#region SegmentTypeEndpoint

class SegmentTypeEndpoint extends Endpoint {
	getAll() {
		return this.axios.get(`${this.baseUrl}/segment-types`)
	}
}

//#region SegmentEndpoint

class SegmentEndpoint extends Endpoint {
	getSegment(eventId, segmentId) {
		return this.axios.get(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}`
		)
	}

	getSegments(eventId, params) {
		return this.axios.get(`${this.baseUrl}/events/${eventId}/segments`, {
			params
		})
	}

	getSegmentsByLevel(eventId, level = 0) {
		return this.axios.get(
			`${this.baseUrl}/events/${eventId}/segments?filter[level]=${level}`
		)
	}

	patch(eventId, segmentId, object) {
		const data = Type.from(
			{ id: segmentId, ...object },
			'Segments'
		).toJson()

		return this.axios.patch(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}`,
			{
				data
			}
		)
	}

	create(eventId, parentId, eventForm, mode) {
		const data = Segment.fromFormToJson(eventForm, parentId, mode)

		return this.axios.post(`${this.baseUrl}/events/${eventId}/segments`, {
			data
		})
	}

	update(eventId, parentId, segmentForm, mode) {
		const data = Segment.fromFormToJson(segmentForm, parentId, mode)

		return this.axios.patch(
			`${this.baseUrl}/events/${eventId}/segments/${segmentForm.id}`,
			{
				data
			}
		)
	}

	delete(eventId, segmentId) {
		return this.axios.delete(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}`
		)
	}

	updateAdvancementRule(eventId, segmentId, advanceRuleForm) {
		const data = AdvanceRule.fromFormToJson(advanceRuleForm)

		return this.axios.put(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/advance-rules/${advanceRuleForm.id}`,
			{
				data
			}
		)
	}

	createAdvancementRule(eventId, segmentId, advanceRuleForm) {
		const data = AdvanceRule.fromFormToJson(advanceRuleForm)

		return this.axios.post(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/advance-rules`,
			{
				data
			}
		)
	}

	deleteAdvancementRule(eventId, segmentId, advanceRuleId) {
		return this.axios.delete(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/advance-rules/${advanceRuleId}`
		)
	}
}

//#region SupportedPeriodEndpoint

class SupportedPeriodEndpoint extends Endpoint {
	update(eventId, segmentId, supportedPeriodForm) {
		const data = SupportedPeriod.fromFormToJson(supportedPeriodForm)

		return this.axios.put(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/supported-periods/${supportedPeriodForm.id}`,
			{
				data
			}
		)
	}

	create(eventId, segmentId, registrationPhaseForm) {
		const data = SupportedPeriod.fromFormToJson(registrationPhaseForm)

		return this.axios.post(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/supported-periods`,
			{
				data
			}
		)
	}

	delete(eventId, segmentId, registrationPhaseId) {
		return this.axios.delete(
			`${this.baseUrl}/events/${eventId}/segments/${segmentId}/supported-periods/${registrationPhaseId}`
		)
	}
}

//#region GamesEndpoint

class GamesEndpoint extends Endpoint {
	getAll(params) {
		return this.axios.get(`${this.baseUrl}/games`, {
			params
		})
	}

	getById(id) {
		return this.axios.get(`${this.baseUrl}/games/${id}`)
	}
}

//#region LeaderboardsEndpoint

class LeaderboardsEndpoint extends Endpoint {
	getAll(params) {
		return this.axios.get(`${this.baseUrl}/leaderboards`, {
			params
		})
	}

	getById(id) {
		return this.axios.get(`${this.baseUrl}/leaderboards/${id}`)
	}

  getEntriesForLeaderboard(leaderboardId, params) {
    return this.axios.get(`${this.baseUrl}/leaderboards/${leaderboardId}/entries`, {
      params,
    })
  }
}

//#region MatchCheckinsEndpoint

class MatchCheckinsEndpoint extends Endpoint {
	getByMatchSeriesId(matchSeriesId) {
		return this.axios.get(
			`${this.baseUrl}/checkins?filter[matchSeries]=${matchSeriesId}`
		)
	}

	checkInPlayer(matchSeriesId) {
		const data = {
			type: 'checkins',
			relationships: {
				matchSeries: {
					data: {
						type: 'match-series',
						id: matchSeriesId
					}
				}
			}
		}

		return this.axios.post(`${this.baseUrl}/checkins`, {
			data
		})
	}
}

//#region MatchBracketEndpoint
class MatchBracketEndpoint extends Endpoint {
	getInfo(segmentId) {
		return this.axios.get(`${this.baseUrl}/segments/${segmentId}/bracket/info`)
	}
}

//#region MatchSeriesEndpoint

class MatchSeriesEndpoint extends Endpoint {
	getSwissRecords(segmentId, params) {
		return this.axios.get(`${this.baseUrl}/segments/${segmentId}/swiss-records`, {
			params,
			headers: {
				Accept: 'application/json'
			}
		})
	}

	getAll(params) {
		return this.axios.get(`${this.baseUrl}/match-series`, {
			params
		})
	}

	getBySegment(segment) {
		return this.axios.get(
			`${this.baseUrl}/match-series?filter[segment]=${segment}`
		)
	}

	get(id) {
		return this.axios.get(`${this.baseUrl}/match-series/${id}`)
	}

	getAllDeleted(params) {
		return this.axios.get(`${this.baseUrl}/match-series/deleted`, {
			params
		})
	}

	reset(id) {
		return this.axios.post(`${this.baseUrl}/match-series/${id}/reset`)
	}

	delete(id) {
		const data = Type.from({ id }, 'MatchSeries').toJson()

		return this.axios.delete(`${this.baseUrl}/match-series/${id}`, {
			data
		})
	}

	restore(id) {
		return this.axios.post(
			`${this.baseUrl}/match-series/deleted/${id}/restore`
		)
	}

	report(id, scores) {
		const data = Type.from(scores, 'matches-series-results').toJson()

		return this.axios.patch(
			`${this.baseUrl}/match-series/${id}/relationships/matches/results`,
			{
				data
			}
		)
	}

	getEvidence(params) {
		return this.axios.get(
			`${this.baseUrl}/match-series/${params.matchSeriesId}/attachments`,
			{
				params
			}
		)
	}

	updateMatchSeries(matchSeries) {
		const data = Type.from(matchSeries, 'match-series').toJson()

		return this.axios.patch(
			`${this.baseUrl}/match-series/${matchSeries.id}`,
			{
				data
			}
		)
	}

	assignRosters(data) {
		return this.axios.patch(`${this.baseUrl}/match-series/assign-rosters`, {
			data
		})
	}

	checkQueueStatus(segmentId) {
		return this.axios.get(`${this.baseUrl}/queue/${segmentId}/status`);
	}

	joinQueue(segmentId) {
		return this.axios.post(`${this.baseUrl}/queue/${segmentId}/join`);
	}

	leaveQueue(segmentId) {
		return this.axios.post(`${this.baseUrl}/queue/${segmentId}/leave`);
	}

	getMatchups(params) {
		return this.axios.get(`${this.baseUrl}/match-ups`, {
			params,
		});
	}

	acceptMatchup(matchUpId) {
		return this.axios.post(`${this.baseUrl}/match-ups/${matchUpId}/accept`);
	}

	declineMatchup(matchUpId) {
		return this.axios.post(`${this.baseUrl}/match-ups/${matchUpId}/decline`);
	}

	getBracket(segmentId, bracketNumber, contentType) {
		if (!contentType) {
			return this.axios.get(`${this.baseUrl}/bracket/${segmentId}/match-series?filter[bracket.number]=${bracketNumber}`)
		}
		return this.axios.get(`${this.baseUrl}/bracket/${segmentId}/match-series?filter[bracket.number]=${bracketNumber}`, {
			headers: {
				'Content-Type': contentType,
				'Accept': contentType,
			}
		});
	}
}

//#region PickBanCategoriesEndpoints

class PickBanCategoriesEndpoints extends Endpoint {
	getAll() {
		return this.axios.get(`${this.baseUrl}/categories`)
	}
}

//#region PickBanChallengesEndpoints

class PickBanChallengesEndpoints extends Endpoint {
	getMine(categoryId) {
		return this.axios.get(`${this.baseUrl}/challenges/mine?filter[category.id]=${categoryId}&filter[status]=open`)
	}

	getByMatchSeries(matchSeriesId) {
		return this.axios.get(`${this.baseUrl}/challenges?filter[matchSeries.id]=${matchSeriesId}`)
	}

	sendPickBans(challengeId, bans = [], picks = []) {
		const data = {
			type: 'votes',
			attributes: {}
		}

		if(bans.length > 0) {
			data.attributes.banned = []
			bans.forEach(b => data.attributes.banned.push({ id: b.id }))
		}

		if(picks.length > 0) {
			data.attributes.picked = []
			picks.forEach(p => data.attributes.picked.push({ id: p.id }))
		}

		return this.axios.post(`${this.baseUrl}/challenges/${challengeId}/vote`, {
			data
		})
	}
}

//#region PickBanItemsEndpoints

class PickBanItemsEndpoints extends Endpoint {
	getByCategory(categoryId, pageSize = 25, page = 1) {
		return this.axios.get(`${this.baseUrl}/items?filter[category.id]=${categoryId}&sort=sortOrder&page[size]=${pageSize}&page[number]=${page}`)
	}
}

//#region RosterCheckinsEndpoint

class RosterCheckinsEndpoint extends Endpoint {
	checkinRoster(rosterId) {
		const data = {
			type: 'checkins',
			relationships: {
				roster: {
					data: {
						type: 'rosters',
						id: rosterId,
					},
				},
			},
		}
		return this.axios.post(`${this.baseUrl}/checkins`, {
			data,
		})
	}
}

//#region RosterCreateEndpoint

class RosterCreateEndpoint extends Endpoint {
	createFromForm(rosterForm) {
		const data = Roster.fromFormToJson(rosterForm)

		return this.axios.post(`${this.baseUrl}/rosters`, {
			data
		})
	}

	createRosterBySegmentId(segmentId) {
		// TODO: maybe refactor this correctly with our json api parser
		const data = {
			type: 'Roster',
			relationships: {
				segment: {
					data: {
						type: 'Segment',
						id: segmentId,
					},
				},
			},
		}
		return this.axios.post(`${this.baseUrl}/rosters`, {
			data,
		})
	}
}

//#region RosterDeleteEndpoint

class RosterDeleteEndpoint extends Endpoint {
	deleteRoster(id) {
		return this.axios.delete(`${this.baseUrl}/rosters/${id}`)
	}
}

//#region RosterManagementEndpoint

class RosterManagementEndpoint extends Endpoint {
	triggerAdvancements(segmentId) {
    return this.axios.post(`${this.baseUrl}/segments/${segmentId}/advance`)
  }

  joinWithCode(code) {
	const data = Type.from(code, 'joining-keys').toJson()
	return this.axios.post(`${this.baseUrl}/rosters/join`, {
		data
	})
  }

  update(rosterId, form) {
	const data = Type.from(form, 'Roster').toJson()

	return this.axios.patch(`${this.baseUrl}/rosters/${rosterId}`, {
		data
	})
  }
}

//#region RosterPlayerEndpoint

class RosterPlayerEndpoint extends Endpoint {
	add(rosterId, form) {
		const data = Players.fromFormToJson(form)

		return this.axios.post(
			`${this.baseUrl}/rosters/${rosterId}/relationships/players`,
			{
				data
			}
		)
	}

	delete(rosterId, playerId) {
		return this.axios.delete(
			`${this.baseUrl}/rosters/${rosterId}/relationships/players/${playerId}`
		)
	}

	leaveRoster(rosterId) {
		return this.axios.post(
			`${this.baseUrl}/rosters/${rosterId}/leave`
		)
	}
}

//#region RosterRetrieveEndpoint

class RosterRetrieveEndpoint extends Endpoint {
	get(params) {
		return this.axios.get(`${this.baseUrl}/rosters`, {
			params
		})
	}

	getBySegment(segment) {
		return this.axios.get(
			`${this.baseUrl}/rosters?filter[segment]=${segment}`
		)
	}

	getRosterByOwner(ownerId, eventId) {
		return this.axios.get(
			`${this.baseUrl}/rosters?filter[event]=${eventId}&filter[owner]=${ownerId}`
		)
	}

	getRosterByPlayer({ userId }) {
		return this.axios.get(`${this.baseUrl}/rosters?filter[user]=${userId}`)
	}

	getRosterById(id) {
		return this.axios.get(`${this.baseUrl}/rosters/${id}`)
	}

	getJoiningKey(rosterId) {
		return this.axios.get(`${this.baseUrl}/rosters/${rosterId}/joining-key`)
	}
}

//#region StandingsManagementEndpoint

class StandingsManagementEndpoint extends Endpoint {
	recalculate(segmentId) {
		return this.axios.post(
			`${this.baseUrl}/segments/${segmentId}/standings/recalculate`
		)
	}
}

//#region StandingsRetrieveEndpoint

class StandingsRetrieveEndpoint extends Endpoint {
	// eslint-disable-next-line max-params
	getBySegment(segmentId, page = 1, pageSize = 20, sort = 'placement') {
		return this.axios.get(
			`${this.baseUrl}/segments/${segmentId}/standings?page[size]=${pageSize}&page[number]=${page}&sort=${sort}`
		)
	}

	getBySegmentAndUser(segmentId, userId) {
		return this.axios.get(
			`${this.baseUrl}/segments/${segmentId}/standings?filter[user]=${userId}`
		)
	}

	getBySegmentAndRoster(segmentId, rosterId) {
		return this.axios.get(
			`${this.baseUrl}/segments/${segmentId}/standings?filter[roster.id]=${rosterId}`
		)
	}

	getPointsByMatchSeries(matchSeriesId) {
		return this.axios.get(
			`${this.baseUrl}/match-series/${matchSeriesId}/standings/points`
		)
	}

	getPointsByRoster(rosterId) {
		return this.axios.get(
			`${this.baseUrl}/roster/${rosterId}/standings/points`
		)
	}
}

//#region TeamsEndpoint

class TeamsEndpoint extends Endpoint {
	getAll(params) {
		return this.axios.get(`${this.baseUrl}/teams`, {
			params
		})
	}

	getById(id) {
		return this.axios.get(`${this.baseUrl}/teams/${id}`)
	}

	create(teamForm) {
		const data = Team.fromFormToJson(teamForm)

		return this.axios.post(`${this.baseUrl}/teams`, {
			data
		})
	}
}

//#region UsersActivitiesEndpoint

class UsersActivitiesEndpoint extends Endpoint {
	getAll(params) {
		return this.axios.get(`${this.baseUrl}/activity-log`, {
			params
		})
	}

	getGroupedForUser(params) {
		return this.axios.get(
			`${this.baseUrl}/activity-log/groups/users/${params.userId}`,
			{
				params
			}
		)
  }
}

//#region UsersAuthenticationEndpoint

class UsersAuthenticationEndpoint extends Endpoint {
	login({ email, password }) {
		return this.axios
			.post(`${this.baseUrl}/login`, {
				data: Type.from({
					email,
					password
				}).toJson()
			})
			.catch(err => {
				throw err.response.data
			})
	}

	getCurrentUser(token) {
		return this.axios.get(`${this.baseUrl}/user`, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		})
	}

	refreshToken(token) {
		return this.axios.post(`${this.baseUrl}/login/refresh`, {}, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		})
	}
}

//#region UsersMeActivityEndpoint

class UsersMeActivityEndpoint extends Endpoint {
	uploadAvatar(avatar) {
		const data = Type.from(avatar, 'user').toJson()
		return this.axios.patch(`${this.baseUrl}/user/avatar`, {
			data
		})
	}

	deleteAvatar() {
		return this.axios.delete(`${this.baseUrl}/user/avatar`)
	}

	changePassword(passwordData) {
		const data = Type.from(passwordData, 'changeUserPassword').toJson()

		return this.axios.post(`${this.baseUrl}/user/password/change`, {
			data
		})
	}
}

//#region UsersOauthEndpoint

class UsersOauthEndpoint extends Endpoint {
	getDiscordAuthUrl(returnUrl) {
		return this.axios.get(`${this.baseUrl}/oauth/discord/authorization_url?return_uri=${returnUrl}`)
	}

	getDiscordAuthToken(returnUrl, code) {
		return this.axios.get(`${this.baseUrl}/oauth/discord/access_token?return_uri=${returnUrl}&code=${code}`)
	}
}

//#region usersPasswordEndpoint

class usersPasswordEndpoint extends Endpoint {
	sendResetEmail(email) {
		const data = Type.from(
			{
				email
			},
			'resetPassword'
		).toJson()

		return this.axios
			.post(`${this.baseUrl}/user/password/reset`, {
				data
			})
			.then(res => res.data)
	}

	resetPassword(token, password) {
		const data = Type.from(
			{
				token,
				password
			},
			'password-reset'
		).toJson()

		return this.axios.post(`${this.baseUrl}/user/password/reset/change`, {
			data
		})
	}

	changePassword(password) {
		const data = Type.from(password, 'changeUserPassword').toJson()

		return this.axios.post(`${this.baseUrl}/user/password/change`, {
			data
		})
	}
}

//#region UsersProfilesEndpoint

class UsersProfilesEndpoint extends Endpoint {
	uploadAvatar(avatar, profileId) {
		const data = Type.from(
			{
				avatar
			},
			'profile'
		).toJson()

		data.id = profileId

		return this.axios.patch(
			`${this.baseUrl}/profiles/${profileId}/avatar`,
			{
				data
			}
		)
	}

	deleteAvatar(profileId) {
		return this.axios.delete(`${this.baseUrl}/profiles/${profileId}/avatar`)
	}
}

//#region UsersEndpoint

class UsersEndpoint extends Endpoint {
	getAll(params) {
		return this.axios.get(`${this.baseUrl}/users`, {
			params
		})
	}

	getUser(userId) {
		return this.axios.get(`${this.baseUrl}/users/${userId}`)
	}

	getGameAccounts({ userId }) {
		return this.axios.get(`${this.baseUrl}/users/${userId}/game-accounts`)
	}

	deleteUser(userId) {
		return this.axios.delete(`${this.baseUrl}/users/${userId}`)
	}

	updateUser(user) {
		const data = Type.from(user, 'users').toJson()

		return this.axios.patch(`${this.baseUrl}/users/${user.id}`, {
			data
		})
	}

	suspendUser(id) {
		return this.axios.post(`${this.baseUrl}/users/${id}/suspend`)
	}

	unsuspendUser(id) {
		return this.axios.post(`${this.baseUrl}/users/${id}/unsuspend`)
	}

	sendPasswordReset(id) {
		return this.axios.post(`${this.baseUrl}/users/${id}/password/reset`)
	}

	sendEmailAddressToken(userId, emailId) {
		return this.axios.post(
			`${this.baseUrl}/users/${userId}/email-addresses/${emailId}/resend`
		)
	}

	addEmail(userId, email) {
		const data = Type.from(email, 'email-addresses').toJson()

		return this.axios.post(
			`${this.baseUrl}/users/${userId}/email-addresses`,
			{
				data
			}
		)
	}

	getEmailAddresses({ userId }) {
		return this.axios.get(`${this.baseUrl}/users/${userId}/email-addresses`)
	}

	getPhoneNumbers({ userId }) {
		return this.axios.get(`${this.baseUrl}/users/${userId}/phone-numbers`)
	}

	sendPhoneVerificationToken(userId, phoneId) {
		return this.axios.post(
			`${this.baseUrl}/users/${userId}/phone-numbers/${phoneId}/resend`
		)
	}

	addPhoneNumber(userId, phoneNumber) {
		const data = Type.from(phoneNumber, 'phone-numbers').toJson()

		return this.axios.post(
			`${this.baseUrl}/users/${userId}/phone-numbers`,
			{
				data
			}
		)
	}

	deleteGameAccount(userId, gameAccountId) {
		return this.axios.delete(
			`${this.baseUrl}/users/${userId}/game-accounts/${gameAccountId}`
		)
	}

	createGameAccount(userId, type, username) {
		const data = Type.from({ username }, 'game-accounts').toJson()

		return this.axios.post(
			`${this.baseUrl}/users/${userId}/game-accounts/${type}`,
			{
				data
			}
		)
	}

	updateGameAccount(userId, type, gameAccount) {
		const data = GameAccount.fromFormToJson(gameAccount)

		return this.axios.patch(
			`${this.baseUrl}/users/${userId}/game-accounts/${type}/${gameAccount.id}`,
			{
				data
			}
		)
	}
}

//#endregion

//#region Global variables
/**
 * Global variables
 */
const FORMATS = {
  DATE_TIME: "dd LLL yyyy HH:mm '(GMT'Z')'",
  DATE: "dd LLL yyyy '(GMT'Z')'",
  TIME: "HH:mm '(GMT'Z')'"
}

const TYPES = [
  {className: ActivityLogUserGrouped, alias: 'activity-log-user-grouped'},
  {className: ActivityLog, alias: 'activity-log'},
  {className: AdvanceRule, alias: 'advance-rules'},
  {className: Attachment, alias: 'attachments'},
  {className: Competitor, alias: 'competitors'},
  {className: EmailAddress, alias: 'email-addresses'},
  {className: Event, alias: 'events'},
  {className: Franchise, alias: 'franchises'},
  {className: GameAccount, alias: 'game-account'},
  {className: Game, alias: 'games'},
  {className: MatchSeries, alias: 'match-series'},
  {className: Match, alias: 'matches'},
  {className: Period, alias: 'periods'},
  {className: PhoneNumber, alias: 'phone-numbers'},
  {className: Players, alias: 'players'},
  {className: RegistrationPhase, alias: 'registration-phases'},
  {className: Role, alias: 'roles'},
  {className: Roster, alias: 'rosters'},
  {className: SegmentType, alias: 'segment-types'},
  {className: Segment, alias: 'segments'},
  {className: SupportedPeriod, alias: 'supported-periods'},
  {className: Team, alias: 'teams'},
  {className: Token, alias: 'token'},
  {className: User, alias: 'user'},
  {className: Users, alias: 'users'}
]

const ENDPOINTS = [
  {className: ChatEndpoint, alias: 'chat', collection: 'chat', version: '1.0'},
  {className: EventEndpoint, alias: 'event', collection: 'events', version: '1.0'},
  {className: RegistrationPhaseEndpoint, alias: 'registration-phases', collection: 'events', version: '1.0'},
  {className: SegmentTypeEndpoint, alias: 'segment-type', collection: 'events', version: '1.0'},
  {className: SegmentEndpoint, alias: 'segment', collection: 'events', version: '1.0'},
  {className: SupportedPeriodEndpoint, alias: 'supported-periods', collection: 'events', version: '1.0'},
  {className: GamesEndpoint, alias: 'games', collection: 'games', version: '1.0'},
  {className: LeaderboardsEndpoint, alias: 'leaderboards', collection: 'leaderboards', version: '1.0'},
  {className: MatchCheckinsEndpoint, alias: 'checkins', collection: 'matches', version: '1.0'},
  {className: MatchBracketEndpoint, alias: 'brackets', collection: 'matches', version: '1.0'},
  {className: MatchSeriesEndpoint, alias: 'match-series', collection: 'matches', version: '1.0'},
  {className: PickBanCategoriesEndpoints, alias: 'categories', collection: 'pickBan', version: '1.0'},
  {className: PickBanChallengesEndpoints, alias: 'challenges', collection: 'pickBan', version: '1.0'},
  {className: PickBanItemsEndpoints, alias: 'items', collection: 'pickBan', version: '1.0'},
  {className: RosterCheckinsEndpoint, alias: 'checkins', collection: 'rosters', version: '1.0'},
  {className: RosterCreateEndpoint, alias: 'create', collection: 'rosters', version: '1.0'},
  {className: RosterDeleteEndpoint, alias: 'delete', collection: 'rosters', version: '1.0'},
  {className: RosterManagementEndpoint, alias: 'management', collection: 'rosters', version: '1.0'},
  {className: RosterPlayerEndpoint, alias: 'players', collection: 'rosters', version: '1.0'},
  {className: RosterRetrieveEndpoint, alias: 'retrieve', collection: 'rosters', version: '1.0'},
  {className: StandingsManagementEndpoint, alias: 'management', collection: 'standings', version: '1.0'},
  {className: StandingsRetrieveEndpoint, alias: 'retrieve', collection: 'standings', version: '1.0'},
  {className: TeamsEndpoint, alias: 'teams', collection: 'teams', version: '1.0'},
  {className: UsersActivitiesEndpoint, alias: 'activity', collection: 'users', version: '1.0'},
  {className: UsersAuthenticationEndpoint, alias: 'authentication', collection: 'users', version: '1.0'},
  {className: UsersMeActivityEndpoint, alias: 'me', collection: 'users', version: '1.0'},
  {className: UsersOauthEndpoint, alias: 'oauth', collection: 'users', version: '1.0'},
  {className: usersPasswordEndpoint, alias: 'password', collection: 'users', version: '1.0'},
  {className: UsersProfilesEndpoint, alias: 'profile', collection: 'users', version: '1.0'},
  {className: UsersEndpoint, alias: 'users', collection: 'users', version: '1.0'}
]
//#endregion

//#region Helper functions
/**
 * Helper functions
 */
function spliceNoMutate (myArray, indexToRemove) {
  return myArray
    .slice(0, indexToRemove)
    .concat(myArray.slice(indexToRemove + 1))
}

function snakeToCamel (str) {
  return str.replace(/([-_][a-z])/g, group =>
    group
      .toUpperCase()
      .replace('-', '')
      .replace('_', '')
  )
}

function toJsonApiType (obj) {
  let res = {}

  if (isNil(obj) || isUndefined(obj)) {
    return obj
  }

  if (typeof obj !== 'object') {
    return obj
  }

  if (obj._type) {
    const typeExists = Boolean(Type._types[obj._type])
    res = typeExists ? new (TYPES.find(t => t.alias === obj._type)).className(obj) : res
  }

  Object.entries(obj).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      res[key] = value.map(v => toJsonApiType(v))
    } else if (typeof value === 'object') {
      res[key] = toJsonApiType(value)
    } else {
      res[key] = value
    }
  })

  return res
}

function toJsonApiStore (store) {
  const newStore = {}

  Object.entries(store.state).forEach(([moduleName, module]) => {
    newStore[moduleName] = toJsonApiType(module)
  })

  return newStore
}

function getErrorMessage (err) {
  return err?.response?.data?.errors
      ?.map?.(error => {
          if (error.violations) {
              return JSON.stringify(error.violations, null, 2)
          } else if (error.detail) {
              const trace =
                  error?.debug?.debug !== undefined
                      ? [
                              `\n${error?.debug?.debug?.file}@${error?.debug?.debug?.line}`,
                              ...error?.debug?.debug?.trace?.map(
                                  t => `\n${t.file}@${t.line}`
                              )
                        ]
                      : ''
              return `${error.title}: ${error.detail}${trace}`
          }
      })
      .join('\n')
}
//#endregion

//#region Interceptors
/**
 * interceptors
 */
function requiresAuthenticationInterceptor (redirect) {
  return (err) => {
    if (err?.response?.status === 401) {
      redirect('/login')
      return Promise.resolve(err)
    }

    return Promise.reject(err)
  }
}

// https://github.com/nuxt-community/modules/issues/89#issuecomment-435038157
function bearerTokenInterceptor (token) {
  return (config) => {
    if(token) {
			let value = null

			if (isString(token)) {
				value = token
			} else if (typeof token === 'object' && token.value) {
				value = token.value
			}

      if (value) {
        config.headers.common.Authorization = `Bearer ${value}`
      }

      return config
    }

		return config
  }
}

function jsonApiTypeCastInterceptor (res) {
	if (res?.headers?.['content-type'] === 'application/json') {
		return res.data
	}

  if (res.data && res.data.data) {
    res.data.data = Type.fromJson(
      res.data.data,
      /**
       * data.included is included twice due to series having a parent and children.
       * This causes infinite recursion when casting the relationship(s) of the
       * included item.
       *
       * The solution is to remove a used data.included before we recurse, this
       * results in the leaf not being set to a rich object however, we thus include
       * it twice.
       */
      res.data.included
        ? [...res.data.included, ...res.data.included]
        : []
    )
  }

  if (res.data && res.data.included) {
    res.data.included = Type.fromJson(res.data.included, res.data.included)
  }

  return res.data
}

function errorInterceptor(app, apiClass) {
	return err => {
		const apiError = new APIError(err)

		apiClass.callHook('error', err)

		return Promise.reject(apiError)
	}
}

function proxyRequestInterceptor(config, proxyUrl) {
	if (config.url && proxyUrl)
		config.url = `${proxyUrl}${encodeURIComponent(config.url)}`

	return config
}

function xStatusCodeInterceptorRequest(config) {
	config.headers.common['X-Status-Code'] = true

	return config
}

function xStatusCodeInterceptorResponse(response) {
	const statusCode =
		response?.headers?.['x-status-code'] ||
		response?.headers?.['X-Status-Code']

	if (statusCode && Number.parseInt(statusCode) > 299) {
		// We actually have an error
		const data = {
			response: {
				...response,
				status: Number.parseInt(statusCode),
			},
		}
		return Promise.reject(data)
	}

	return response
}
//#endregion

//#region Api
/**
 * Api Class
 */

class Api extends Hookable {
  registerAllEndpoints (axios, apiBaseUrl) {
    ENDPOINTS.forEach((endpoint) => {
      const collectionName = endpoint.collection
      const collectionVersion = endpoint.version
	  const collectionUrl = this.camelCaseToDash(collectionName)
      const fileName = endpoint.alias
      const baseUrl = `${apiBaseUrl}/api/${collectionUrl}/${collectionVersion}`

      Object.assign(this, {
        [collectionName]: {
          ...this[collectionName],
          [snakeToCamel(fileName)]: new endpoint.className(axios, baseUrl)
        }
      })
    })
  }

  camelCaseToDash (str) {
		return str.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase()
  }

  setupTokenRefresh (store) {
    const delay = 1 * 60 * 1000

    if (process.client && store) {
      if (store.getters['user/isLoggedIn']) {
        const expirationDate = store.state.user.token.expires
        const tokenExpiry = new Date(
          expirationDate.replace(/([+-]\d\d)(\d\d)$/, '$1:$2')
        )
        const tokenExpiresInMillis = tokenExpiry.getTime() - Date.now()
        const paddedTokenTimeout = tokenExpiresInMillis - delay
        this.tokenRefreshTimeout = setTimeout(async () => {
          await store.dispatch(
            'user/refreshToken',
            store.state.user.token.value
          )

          this.setupTokenRefresh(store)
        }, paddedTokenTimeout)
      }
    }
  }

  clearTokenRefresh () {
    clearTimeout(this.tokenRefreshTimeout)
  }

  /**
    * Vue-ssr does not pass over the instances of types to the UI, thus we
    * need to redo the instantiation of types when the browser hydrates the
    * store.
    * @param store
    */
  castStoreToTypes (store) {
    if (process.client && store) {
      store.replaceState(toJsonApiStore(store))
    }
  }

	setContentType (type) {
		const contentType = type === 'json' ? 'application/json' : 'application/vnd.api+json'
		this.axios.defaults.headers.common.Accept = contentType
    this.axios.defaults.headers['content-type'] = contentType
	}

	setCustomHeader (header, value) {
		this.axios.defaults.headers.common[header] = value
	}

  setTenantHeader (tenant) {
    this.axios.defaults.headers.common['X-Origin'] = tenant
  }

  setBearerToken (token) {
		this.token = token
		this.bearerTokenInterceptor = this.axios.interceptors.request.use(bearerTokenInterceptor(this.token))
  }

  removeBearerToken () {
	  this.token = null
	  this.axios.interceptors.request.eject(this.bearerTokenInterceptor)
	  this.bearerTokenInterceptor = null
  }

	setXStatusCodeInterceptor() {
		this.xStatusCodeInterceptorResponse = this.axios.interceptors.response.use(xStatusCodeInterceptorResponse)
		this.xStatusCodeInterceptorRequest = this.axios.interceptors.request.use(xStatusCodeInterceptorRequest)
	}

	setProxyRequestInterceptor(proxyUrl) {
		this.proxyRequestInterceptor = this.axios.interceptors.request.use((config) => proxyRequestInterceptor(config, proxyUrl))
	}

	removeXStatusCodeInterceptor() {
		this.axios.interceptors.response.eject(this.xStatusCodeInterceptorResponse)
		this.xStatusCodeInterceptorResponse = null

		this.axios.interceptors.request.eject(this.xStatusCodeInterceptorRequest)
		this.xStatusCodeInterceptorRequest = null
	}

	removeProxyRequestInterceptor() {
		this.axios.interceptors.request.eject(this.proxyRequestInterceptor)
		this.proxyRequestInterceptor = null
	}

  constructor (ctx) {
		super()

		this.token = null
    // Set axios to a global variable so that the headers can be changed outside of the constructor
    this.axios = ctx.$axios
    ctx.$config = ctx.$config || {} // fallback for Nuxt < 2.13
    const runtimeConfig = ctx.$config.gfinityEsportsTools || {}
    const url = runtimeConfig.url
    const tenant = runtimeConfig.tenant
    const disableOpenSesame = runtimeConfig.tenant
		const proxyUrl = runtimeConfig.proxyUrl || undefined
		const enableXStatusCode = runtimeConfig.enableXStatusCode || false
		const contentType = runtimeConfig.contentType || 'vnd.api+json'

    this.axios.defaults.headers.common['X-Origin'] = tenant || '_global_'

    if (!disableOpenSesame) {
      this.axios.defaults.headers.common.opensesame = '971081'
    }

		this.setContentType(contentType)
    Type.registerAllTypes()

		if (enableXStatusCode) {
			this.setXStatusCodeInterceptor()
		}

		if (proxyUrl) {
			this.setProxyRequestInterceptor(proxyUrl)
		}

    this.axios.interceptors.response.use(c => c, errorInterceptor(ctx.app, this))
    this.axios.interceptors.response.use(jsonApiTypeCastInterceptor)

    this.registerAllEndpoints(this.axios, url)

    this.setupTokenRefresh(ctx.store)
    this.castStoreToTypes(ctx.store)

    /**
    * asyncData doesn't get hydrated/have it's JSON:API classes instantiated,
    * we thus do it manually - the same with hydrating the store.
    */
    Vue.mixin({
      beforeMount () {
        const isPage = ctx?.$parent?.$options?.name?.toLocaleLowerCase() === 'nuxt'

        if (isPage) {
          Object.entries(ctx.$data).forEach(([key, value]) => {
            const res = Array.isArray(value)
              ? value.map(toJsonApiType)
              : toJsonApiType(value)

            Vue.set(ctx, key, res)
          })
        }
      }
    })
  }
}

export default function (ctx, inject) {
  const api = new Api(ctx)
  ctx.$api = api
  inject('api', api)
}
